import React from 'react';
import { TreeItem } from '@mui/x-tree-view/TreeItem';
import { TreeView } from '@mui/x-tree-view/TreeView';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import { Location, Place } from '../../../types';
import { Typography, IconButton, Box } from '@mui/material';

const isLocation = (item: Location | Place): item is Location => {
  return 'level' in item;
};

const isPlace = (item: Location | Place): item is Place => {
  return !isLocation(item);
};

// Helper function to get ID from either string or Location object
const getLocationId = (locationRef: string | Location | undefined): string | null => {
  if (!locationRef) return null;
  if (typeof locationRef === 'string') return locationRef;
  return locationRef._id?.toString() || null;
};

// Helper function to get parent ID from a location
const getParentId = (location: Location): string | null => {
  return getLocationId(location.parent);
};

// Whether a city (by id) has at least one NEIGHBORHOOD child anywhere in the
// nested tree. Used to decide if a neighborhood-less place is legitimately
// parented by its city (city with no neighborhoods) vs. genuinely orphaned.
const cityContainsNeighborhood = (cityId: string, tree: (Location | Place)[]): boolean => {
  for (const node of tree) {
    if (!isLocation(node)) continue;
    if (
      node.level.toUpperCase() === 'NEIGHBORHOOD' &&
      getLocationId(node.parent) === cityId
    ) {
      return true;
    }
    if (node.children && cityContainsNeighborhood(cityId, node.children)) return true;
  }
  return false;
};

// Helper function to find a location by ID in the tree
const findLocationById = (locationId: string | null, tree: (Location | Place)[]): Location | null => {
  if (!locationId) return null;
  for (const node of tree) {
    if (isLocation(node)) {
      if (node._id === locationId) return node;
      if (node.children) {
        const found = findLocationById(locationId, node.children);
        if (found) return found;
      }
    }
  }
  return null;
};

interface LocationTreeProps {
  nodes: (Location | Place)[];
  selectedLocation?: string;
  onNodeSelect: (nodeId: string) => void;
  getLocationIssues: (node: Location | Place, locationTree: (Location | Place)[]) => string[];
  onToggleVisibility?: (node: Location) => void;
  onToggleFavorite?: (node: Location) => void;
  visibleLocations?: string[];
  favoriteLocations?: string[];
  showFavoritesOnly?: boolean;
}

const LocationTree: React.FC<LocationTreeProps> = ({
  nodes,
  selectedLocation,
  onNodeSelect,
  getLocationIssues,
  onToggleVisibility,
  onToggleFavorite,
  visibleLocations = [],
  favoriteLocations = [],
  showFavoritesOnly = false,
}) => {
  // Filter root nodes to only include Locations
  const rootNodes = nodes.filter(node => isLocation(node));

  const validateLocationHierarchy = (node: Location | Place, tree: (Location | Place)[]): string[] => {
    const issues: string[] = [];
    
    if (isPlace(node)) {
      // Places validate their neighborhood assignment. But a place legitimately
      // has NO neighborhood when its city has none — those should nest under the
      // city and not be flagged. Only flag a missing neighborhood when the city
      // actually contains neighborhoods.
      if (!node.neighborhood) {
        const cityId = getLocationId(node.city);
        const cityHasNeighborhoods = !!cityId && cityContainsNeighborhood(cityId, tree);
        if (cityHasNeighborhoods) {
          issues.push('Place has no neighborhood assigned');
        }
      } else {
        const neighborhoodId = getLocationId(node.neighborhood);
        const parentNeighborhood = findLocationById(neighborhoodId, tree);
        if (!parentNeighborhood) {
          issues.push('Assigned neighborhood not found');
        }
      }
      return issues;
    }

    // Location validation
    const location = node as Location;
    const parentId = getParentId(location);
    const parent = parentId ? findLocationById(parentId, tree) : null;

    // Skip validation for GLOBAL and REGION levels
    if (location.level.toUpperCase() === 'GLOBAL' || location.level.toUpperCase() === 'REGION') {
      return issues;
    }

    // Check if this is a root node that shouldn't have a parent
    if (location.level.toUpperCase() === 'COUNTRY' && parent) {
      issues.push(`${location.level} should not have a parent`);
      return issues;
    }

    // For non-root nodes, validate they have the correct parent type
    const validRelationships = [
      { child: 'NEIGHBORHOOD', parent: 'CITY' },
      { child: 'CITY', parent: 'STATE' },
      { child: 'STATE', parent: 'COUNTRY' }
    ];

    const relationship = validRelationships.find(r => r.child === location.level.toUpperCase());
    if (relationship) {
      if (!parent) {
        issues.push(`${location.level} must have a ${relationship.parent} parent`);
      } else if (parent.level.toUpperCase() !== relationship.parent) {
        issues.push(`${location.level}'s parent must be a ${relationship.parent}, found ${parent.level}`);
      }
    }

    return issues;
  };


  const renderNode = (node: Location | Place, isTopLevel: boolean = true) => {
    // Skip places at top level - they should only appear under neighborhoods
    if (isTopLevel && isPlace(node)) {
      return null;
    }

    const issues = validateLocationHierarchy(node, nodes);
    const hasIssues = issues.length > 0;
    const hasCoordinates = isPlace(node) 
      ? node.location?.coordinates && Array.isArray(node.location.coordinates)
      : node.coordinates?.coordinates && Array.isArray(node.coordinates.coordinates);

    const isVisible = visibleLocations.includes(node._id);
    const isFavorite = favoriteLocations.includes(node._id);
    const showEyeIcon = isLocation(node) && node.level.toUpperCase() === 'NEIGHBORHOOD' && onToggleVisibility;
    const showStarIcon = isLocation(node) && node.level.toUpperCase() === 'NEIGHBORHOOD' && onToggleFavorite;

    // Places render as leaves under their neighborhood (so you can drill down
    // country → state → city → neighborhood → place). In the favorites view we
    // still hide them (handled below).
    // (Top-level places are already skipped above.)

    // For favorites menu, only show favorited neighborhoods and their parent hierarchy
    if (showFavoritesOnly) {
      if (isPlace(node)) {
        return null;
      }
      if (node.level.toUpperCase() === 'NEIGHBORHOOD' && !isFavorite) {
        return null;
      }
      if (node.level.toUpperCase() !== 'NEIGHBORHOOD' && node.children) {
        const hasAnyFavoriteDescendants = (node: Location): boolean => {
          if (favoriteLocations.includes(node._id)) return true;
          return (node.children || []).some((child: Location | Place) => 
            isLocation(child) ? hasAnyFavoriteDescendants(child) : false
          );
        };
        if (!hasAnyFavoriteDescendants(node)) {
          return null;
        }
      }
    }

    // Get associated places for neighborhoods. Places are attached to the
    // neighborhood's `children` by the tree builder; also fall back to scanning
    // the flat `nodes` list. `neighborhood` may be a populated object or an ID
    // string, so normalize via getLocationId before comparing.
    const getAssociatedPlaces = (neighborhoodId: string): Place[] => {
      if (!isLocation(node) || node.level.toUpperCase() !== 'NEIGHBORHOOD') return [];
      return nodes.filter((item): item is Place =>
        !isLocation(item) &&
        getLocationId(item.neighborhood) === neighborhoodId
      );
    };

    const isNeighborhood = isLocation(node) && node.level.toUpperCase() === 'NEIGHBORHOOD';

    // Check if node has any children or associated places
    const hasChildren = isLocation(node) && (
      (node.children && node.children.length > 0) ||
      (isNeighborhood && getAssociatedPlaces(node._id).length > 0)
    );

    return (
      <TreeItem 
        key={node._id} 
        nodeId={node._id.toString()} 
        expandIcon={hasChildren ? <ChevronRightIcon /> : null}
        collapseIcon={hasChildren ? <ExpandMoreIcon /> : null}
        label={
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center',
            width: '100%',
            overflow: 'hidden'
          }}>
            <Typography
              component="div"
              sx={{
                color: hasIssues ? 'error.main' : 
                       !hasCoordinates && isLocation(node) ? 'warning.main' : 
                       'inherit',
                fontSize: '0.875rem',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                flex: '1 1 auto'
              }}
            >
              {node.name}
              {hasIssues && (
                <Typography 
                  component="span" 
                  color="error" 
                  sx={{ 
                    ml: 0.5,
                    fontSize: '0.875rem',
                    fontWeight: 'bold'
                  }}
                >
                  !
                </Typography>
              )}
            </Typography>
            <Box sx={{ 
              display: 'flex',
              gap: 0,
              ml: 1,
              '& .MuiIconButton-root': {
                padding: '1px'
              }
            }}>
              {showStarIcon && (
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleFavorite(node as Location);
                  }}
                >
                  {isFavorite ? 
                    <StarIcon sx={{ color: 'gold', fontSize: '0.9rem' }} /> : 
                    <StarBorderIcon sx={{ fontSize: '0.9rem' }} />
                  }
                </IconButton>
              )}
              {showEyeIcon && (
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleVisibility(node as Location);
                  }}
                >
                  {isVisible ? 
                    <VisibilityIcon sx={{ fontSize: '0.9rem' }} /> : 
                    <VisibilityOffIcon sx={{ fontSize: '0.9rem' }} />
                  }
                </IconButton>
              )}
            </Box>
          </Box>
        }
        sx={{
          '& .MuiTreeItem-content': {
            padding: '0px 0'
          },
          '& .MuiTreeItem-label': {
            padding: 0
          },
          overflow: 'hidden'
        }}
      >
        {isLocation(node) && (
          <>
            {/* Render child locations AND places (the tree builder nests places
                into their neighborhood's children). */}
            {node.children?.map((child: Location | Place) => renderNode(child, false))}

            {/* Fallback: render any places associated with this neighborhood
                that weren't already nested into children. */}
            {isNeighborhood &&
              getAssociatedPlaces(node._id)
                .filter((place) => !(node.children || []).some((c) => c._id === place._id))
                .map((place) => renderNode(place, false))}
          </>
        )}
      </TreeItem>
    );
  };

  // Partition root-level items into the "real" hierarchy vs. orphans so orphans
  // don't clog the sidebar. A root item is an orphan when it's a place (places
  // should live under a location) or a non-top location level (only
  // COUNTRY/GLOBAL/REGION belong at the root) or it has validation issues.
  const isValidRoot = (node: Location | Place): boolean => {
    if (isPlace(node)) return false;
    const lvl = node.level.toUpperCase();
    const isTopLevelType = lvl === 'COUNTRY' || lvl === 'GLOBAL' || lvl === 'REGION';
    if (!isTopLevelType) return false;
    return validateLocationHierarchy(node, nodes).length === 0;
  };

  // Places pushed to the tree root by the builder are only Locations after the
  // `rootNodes` filter above — but orphaned places live in `nodes` too. Include
  // any root-level places (they were filtered out of rootNodes).
  const rootPlaces = nodes.filter((n): n is Place => isPlace(n));

  const normalRoots = rootNodes.filter(isValidRoot);
  const orphanRoots: (Location | Place)[] = [
    ...rootNodes.filter((n) => !isValidRoot(n)),
    ...rootPlaces,
  ];

  const orphanCount = orphanRoots.length;

  return (
    <TreeView
      selected={selectedLocation || ''}
      defaultExpanded={['favorites-root']}
      onNodeSelect={(event: React.SyntheticEvent, nodeId: string) => {
        if (nodeId === 'favorites-root' || nodeId === 'orphans-root') return;
        onNodeSelect(nodeId);
      }}
      sx={{ 
        height: '100%',
        flexGrow: 1,
        maxWidth: 300,
        overflowY: 'auto',
        overflowX: 'hidden'
      }}
    >
      {normalRoots.map(node => renderNode(node))}

      {orphanCount > 0 && (
        <TreeItem
          nodeId="orphans-root"
          expandIcon={<ChevronRightIcon />}
          collapseIcon={<ExpandMoreIcon />}
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', py: 0.25 }}>
              <Typography
                component="div"
                sx={{ color: 'error.main', fontWeight: 700, fontSize: '0.8rem', flex: '1 1 auto' }}
              >
                ⚠️ Orphaned / Needs attention
              </Typography>
              <Typography
                component="span"
                sx={{
                  color: 'error.contrastText',
                  bgcolor: 'error.main',
                  borderRadius: '10px',
                  px: 0.75,
                  fontSize: '0.7rem',
                  fontWeight: 700,
                  ml: 1,
                }}
              >
                {orphanCount}
              </Typography>
            </Box>
          }
          sx={{
            '& > .MuiTreeItem-content': { py: 0.5 },
          }}
        >
          {orphanRoots.map((node) => renderNode(node, false))}
        </TreeItem>
      )}
    </TreeView>
  );
};

export default LocationTree; 